import React, { useEffect, useRef, useState } from 'react';
import { APIProvider, Map, AdvancedMarker, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { Clock, Navigation, MapPin } from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Live route polyline from driver to destination
const LiveRoutePolyline = ({ driverLocation, destination }) => {
  const map = useMap();
  const routesLibrary = useMapsLibrary('routes');
  const polylinesRef = useRef([]);
  const lastKeyRef = useRef('');

  useEffect(() => {
    if (!map || !routesLibrary || !driverLocation || !destination) return;

    // Throttle: only re-route if driver moved significantly
    const key = `${driverLocation.latitude.toFixed(4)},${driverLocation.longitude.toFixed(4)}`;
    if (key === lastKeyRef.current) return;
    lastKeyRef.current = key;

    let active = true;
    const orig = { lat: driverLocation.latitude, lng: driverLocation.longitude };
    const dest = { lat: destination.latitude, lng: destination.longitude };

    const cleanup = () => {
      polylinesRef.current.forEach(p => p.setMap(null));
      polylinesRef.current = [];
    };

    const drawPolyline = (path) => {
      if (!active) return;
      cleanup();
      const polyline = new google.maps.Polyline({
        path,
        strokeColor: '#2563eb',
        strokeWeight: 5,
        strokeOpacity: 0.85,
        map,
        zIndex: 10
      });
      polylinesRef.current = [polyline];
    };

    const computeRoute = async () => {
      if (routesLibrary.Route) {
        try {
          const { routes } = await routesLibrary.Route.computeRoutes({
            origin: orig, destination: dest, travelMode: 'DRIVING', fields: ['path'],
          });
          if (routes?.length && routes[0].path) { drawPolyline(routes[0].path); return; }
        } catch (err) {
          // fallback
        }
      }
      const service = new routesLibrary.DirectionsService();
      service.route({ origin: orig, destination: dest, travelMode: google.maps.TravelMode.DRIVING }, (result, status) => {
        if (!active || status !== google.maps.DirectionsStatus.OK || !result) return;
        drawPolyline(result.routes[0].overview_path);
      });
    };

    computeRoute();
    return () => { active = false; cleanup(); };
  }, [map, routesLibrary, driverLocation, destination]);

  return null;
};

// Bounds fitter that includes driver location
const TrackingBoundsFitter = ({ pickupCoords, dropoffCoords, driverLocation }) => {
  const map = useMap();

  useEffect(() => {
    if (!map) return;
    const bounds = new google.maps.LatLngBounds();
    let hasPoints = false;
    if (pickupCoords) { bounds.extend({ lat: pickupCoords.latitude, lng: pickupCoords.longitude }); hasPoints = true; }
    if (dropoffCoords) { bounds.extend({ lat: dropoffCoords.latitude, lng: dropoffCoords.longitude }); hasPoints = true; }
    if (driverLocation) { bounds.extend({ lat: driverLocation.latitude, lng: driverLocation.longitude }); hasPoints = true; }
    if (hasPoints) {
      setTimeout(() => map.fitBounds(bounds, { padding: 60 }), 100);
    }
  }, [map, pickupCoords, dropoffCoords, driverLocation]);

  return null;
};

const TrackingMap = ({ 
  pickupCoords, 
  dropoffCoords, 
  driverLocation, 
  status, 
  eta 
}) => {
  const getStatusColor = () => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      arriving: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-purple-100 text-purple-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status] || colors.pending;
  };

  const getStatusLabel = () => {
    const labels = {
      pending: 'Finding Driver',
      accepted: 'Driver Accepted',
      arriving: 'Driver Arriving',
      in_progress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
    };
    return labels[status] || 'Pending';
  };

  // Determine route destination based on status
  const routeDestination = (status === 'in_progress') ? dropoffCoords : pickupCoords;

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className="relative h-[60vh] bg-gray-200 flex items-center justify-center">
        <p className="text-gray-600">Map not available — API key not configured</p>
      </div>
    );
  }

  const getCenter = () => {
    if (driverLocation) return { lat: driverLocation.latitude, lng: driverLocation.longitude };
    if (pickupCoords) return { lat: pickupCoords.latitude, lng: pickupCoords.longitude };
    return { lat: -10.2129, lng: 31.1802 };
  };

  return (
    <div className="relative h-[60vh]" data-testid="tracking-map">
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
        <Map
          defaultZoom={14}
          defaultCenter={getCenter()}
          mapId="tekstar_tracking_map"
          gestureHandling="greedy"
          disableDefaultUI={false}
          zoomControl={true}
          streetViewControl={false}
          mapTypeControl={false}
          fullscreenControl={false}
          style={{ width: '100%', height: '100%' }}
        >
          <TrackingBoundsFitter pickupCoords={pickupCoords} dropoffCoords={dropoffCoords} driverLocation={driverLocation} />

          {/* Pickup Marker */}
          {pickupCoords && (
            <AdvancedMarker position={{ lat: pickupCoords.latitude, lng: pickupCoords.longitude }}>
              <div className="relative">
                <div className="w-8 h-8 bg-emerald-500 rounded-full border-2 border-white shadow-lg flex items-center justify-center">
                  <MapPin className="h-4 w-4 text-white" />
                </div>
                <div className="absolute -bottom-7 left-1/2 -translate-x-1/2 whitespace-nowrap">
                  <div className="bg-white px-2 py-0.5 rounded shadow text-xs font-semibold text-emerald-700">Pickup</div>
                </div>
              </div>
            </AdvancedMarker>
          )}

          {/* Dropoff Marker */}
          {dropoffCoords && (
            <AdvancedMarker position={{ lat: dropoffCoords.latitude, lng: dropoffCoords.longitude }}>
              <div className="relative">
                <div className="w-8 h-8 bg-red-500 rounded-full border-2 border-white shadow-lg flex items-center justify-center">
                  <MapPin className="h-4 w-4 text-white" />
                </div>
                <div className="absolute -bottom-7 left-1/2 -translate-x-1/2 whitespace-nowrap">
                  <div className="bg-white px-2 py-0.5 rounded shadow text-xs font-semibold text-red-700">Dropoff</div>
                </div>
              </div>
            </AdvancedMarker>
          )}

          {/* Driver Marker — Live Position */}
          {driverLocation && (
            <AdvancedMarker position={{ lat: driverLocation.latitude, lng: driverLocation.longitude }}>
              <div className="relative" data-testid="driver-marker">
                <div className="w-11 h-11 bg-blue-600 rounded-full border-[3px] border-white shadow-xl flex items-center justify-center z-20 relative">
                  <Navigation className="h-5 w-5 text-white" />
                </div>
                {/* Pulsing ring */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-blue-400 rounded-full opacity-25 animate-ping z-10"></div>
              </div>
            </AdvancedMarker>
          )}

          {/* Live route from driver to destination */}
          {driverLocation && routeDestination && status !== 'completed' && status !== 'cancelled' && status !== 'pending' && (
            <LiveRoutePolyline driverLocation={driverLocation} destination={routeDestination} />
          )}
        </Map>
      </APIProvider>

      {/* Status Overlay */}
      <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10">
        <div className={`px-4 py-2 rounded-full ${getStatusColor()} font-semibold shadow-lg`} data-testid="ride-status-badge">
          {getStatusLabel()}
        </div>
      </div>

      {/* ETA Badge */}
      {eta !== null && status !== 'completed' && status !== 'pending' && (
        <div className="absolute top-16 left-1/2 transform -translate-x-1/2 z-10" data-testid="eta-badge">
          <div className="bg-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
            <Clock className="h-4 w-4 text-blue-600" />
            <span className="font-semibold">{eta} min away</span>
          </div>
        </div>
      )}

      {/* Live indicator */}
      {driverLocation && status !== 'completed' && status !== 'cancelled' && (
        <div className="absolute bottom-4 left-4 z-10" data-testid="live-indicator">
          <div className="bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-lg flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
            </span>
            <span className="text-xs font-medium text-gray-700">Live Tracking</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackingMap;
